<template>
  <div class="rich-text-editor">
    <div v-if="editor" class="rte-toolbar">
      <div v-for="(group, index) in toggleGroups" :key="index" class="btn-group" role="group">
        <button
            v-for="item in group"
            :key="item.title"
            type="button"
            :class="['btn', 'btn-sm', 'btn-outline-secondary', item.labelClass, { active: editor.isActive(item.name, item.attrs) }]"
            :title="item.title"
            @click="item.run(editor.chain().focus()).run()"
        >{{ item.label }}</button>
      </div>
      <div class="btn-group" role="group">
        <button
            type="button"
            :class="['btn', 'btn-sm', 'btn-outline-secondary', { active: editor.isActive('link') }]"
            title="Add or edit link"
            @click="setLink"
        >Link</button>
        <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            title="Remove link"
            :disabled="!editor.isActive('link')"
            @click="editor.chain().focus().unsetLink().run()"
        >Unlink</button>
      </div>
      <div class="btn-group" role="group">
        <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            title="Insert image"
            :disabled="imageUploading"
            @click="promptImage"
        >{{ imageUploading ? 'Uploading…' : 'Image' }}</button>
      </div>
    </div>
    <editor-content :editor="editor" class="rte-content" />
    <input
        ref="imageInput"
        type="file"
        accept="image/*"
        class="rte-hidden-input"
        @change="onImageSelected"
    >
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Markdown } from '@tiptap/markdown';
import axios from 'axios';
import { IMG_API_URL, getS3Url, resizeImageFile } from '@/helpers/utils';

// Simple toggle buttons. Link/Unlink/Image are irregular and stay hand-written in the template.
const TOGGLE_GROUPS = [
  [
    { name: 'bold', title: 'Bold', label: 'B', labelClass: 'fw-bold', run: (c) => c.toggleBold() },
    { name: 'italic', title: 'Italic', label: 'I', labelClass: 'fst-italic', run: (c) => c.toggleItalic() },
  ],
  [
    { name: 'heading', attrs: { level: 2 }, title: 'Heading', label: 'H2', run: (c) => c.toggleHeading({ level: 2 }) },
    { name: 'heading', attrs: { level: 3 }, title: 'Sub-heading', label: 'H3', run: (c) => c.toggleHeading({ level: 3 }) },
  ],
  [
    { name: 'bulletList', title: 'Bullet list', label: '• List', run: (c) => c.toggleBulletList() },
    { name: 'orderedList', title: 'Numbered list', label: '1. List', run: (c) => c.toggleOrderedList() },
    { name: 'blockquote', title: 'Quote', label: '“ Quote', run: (c) => c.toggleBlockquote() },
  ],
];

export default {
  name: 'RichTextEditor',
  components: { EditorContent },
  props: {
    modelValue: { type: String, default: '' },
  },
  emits: ['update:modelValue', 'imageUploadError'],
  data() {
    return {
      editor: null,
      imageUploading: false,
      toggleGroups: TOGGLE_GROUPS,
    };
  },
  watch: {
    // Skip values we emitted ourselves so typing doesn't reset the cursor; only re-parse external changes.
    modelValue(value) {
      if (!this.editor) return;
      const next = value || '';
      if (next === this.lastEmitted) return;
      if (next !== this.editor.getMarkdown()) {
        this.editor.commands.setContent(next, { contentType: 'markdown', emitUpdate: false });
      }
    },
  },
  mounted() {
    // Non-reactive; lets the watcher skip values we emitted ourselves.
    this.lastEmitted = this.modelValue || '';
    this.editor = new Editor({
      content: this.modelValue || '',
      contentType: 'markdown',
      extensions: [
        StarterKit.configure({
          link: {
            openOnClick: false,
            autolink: true,
            HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
          },
        }),
        Image.configure({
          inline: false,
          HTMLAttributes: { style: 'max-width: 100%; height: auto;' },
        }),
        Markdown,
      ],
      onUpdate: () => {
        this.lastEmitted = this.editor.getMarkdown();
        this.$emit('update:modelValue', this.lastEmitted);
      },
    });
  },
  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy();
    }
  },
  methods: {
    setLink() {
      const previous = this.editor.getAttributes('link').href;
      const url = window.prompt('Enter the URL', previous || 'https://');
      if (url === null) return;
      if (url === '') {
        this.editor.chain().focus().unsetLink().run();
        return;
      }
      this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    },
    promptImage() {
      this.$refs.imageInput.click();
    },
    onImageSelected(event) {
      const file = event.target.files && event.target.files[0];
      // reset so selecting the same file again re-triggers change
      event.target.value = '';
      if (!file) return;
      this.uploadImage(file);
    },
    async uploadImage(file) {
      this.imageUploading = true;
      try {
        const resized = await resizeImageFile(file);
        const formData = new FormData();
        formData.append('file', resized, file.name);
        const { data } = await axios.post(IMG_API_URL, formData);
        const src = getS3Url(data.key);
        this.editor.chain().focus().setImage({ src, alt: file.name }).run();
      } catch (error) {
        this.$emit('imageUploadError', error);
      } finally {
        this.imageUploading = false;
      }
    },
  },
};
</script>

<style scoped>
.rich-text-editor {
  border: 1px solid #ced4da;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}

.rte-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem;
  border-bottom: 1px solid #ced4da;
  background: #f8f9fa;
}

.rte-toolbar .btn.active {
  background-color: #6c757d;
  color: #fff;
}

.rte-hidden-input {
  display: none;
}

/* Editing surface — roughly mirrors how MarkdownRenderer renders published posts. */
.rte-content {
  min-height: 35vh;
  padding: 1rem;
  overflow-y: auto;
}

.rte-content :deep(.ProseMirror) {
  min-height: calc(35vh - 2rem);
  outline: none;
}

.rte-content :deep(.ProseMirror img) {
  max-width: 100%;
  height: auto;
}

.rte-content :deep(.ProseMirror blockquote) {
  border-left: 3px solid #ced4da;
  margin-left: 0;
  padding-left: 1rem;
  color: #6c757d;
}

/* Placeholder-style hint when empty */
.rte-content :deep(.ProseMirror p.is-editor-empty:first-child::before) {
  content: 'Write your post…';
  float: left;
  color: #adb5bd;
  pointer-events: none;
  height: 0;
}
</style>
